import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateReportBody,
  EditReportBody,
  RefreshReportBody,
  RenameReportBody,
  ReportPreferencesBody,
  ReviseReportBody,
  UpdateReportInputsBody,
} from '@ni/shared';
import { useApi, useOrg } from './app-context';
import { PAGE_POLL_MS, RUN_POLL_MS, qk } from './keys';

/**
 * Reports' reads and writes (docs/notes/2026-09-22-reports-design.md §2).
 *
 * The report page polls while a version is being prepared and stops when it is
 * not: the run strip's cadence, off the report's own `active_run`, so the page
 * needs no second read of the run to know when to stop.
 */
/** `soon`: a report is expected shortly (one being written), so poll at the run cadence. */
export function useReports({ soon = false }: { soon?: boolean } = {}) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.reports(org.id),
    queryFn: () => api.listReports(),
    refetchInterval: soon ? RUN_POLL_MS : PAGE_POLL_MS,
  });
}

/** Null reads nothing. */
export function useReport(reportId: string | null) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.report(org.id, reportId ?? ''),
    queryFn: () => api.getReport(reportId as string),
    enabled: reportId !== null,
    refetchInterval: (query) => (query.state.data?.active_run ? RUN_POLL_MS : false),
  });
}

export function useReportVersion(reportId: string, number: number | null) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: [...qk.report(org.id, reportId), 'version', number ?? 0],
    queryFn: () => api.getReportVersion(reportId, number as number),
    enabled: number !== null,
  });
}

/** The version's PDF, for the page to save. Starts no run and writes nothing. */
export function useReportExport(reportId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (number: number) => api.exportReport(reportId, number),
  });
}

function useInvalidateReports() {
  const queryClient = useQueryClient();
  const org = useOrg();
  return () => queryClient.invalidateQueries({ queryKey: qk.reportsPrefix(org.id) });
}

export function useCreateReport() {
  const api = useApi();
  const invalidate = useInvalidateReports();
  return useMutation({
    mutationFn: (body: CreateReportBody) => api.createReport(body),
    onSettled: () => void invalidate(),
  });
}

/** Every write on one report, each moving the list and the page. */
export function useReportWrites(reportId: string) {
  const api = useApi();
  const invalidate = useInvalidateReports();
  const settle = { onSettled: () => void invalidate() };
  return {
    revise: useMutation({
      mutationFn: (body: ReviseReportBody) => api.reviseReport(reportId, body),
      ...settle,
    }),
    updateInputs: useMutation({
      mutationFn: (body: UpdateReportInputsBody) => api.updateReportInputs(reportId, body),
      ...settle,
    }),
    refresh: useMutation({
      mutationFn: (body: RefreshReportBody) => api.refreshReport(reportId, body),
      ...settle,
    }),
    edit: useMutation({
      mutationFn: (body: EditReportBody) => api.editReport(reportId, body),
      ...settle,
    }),
    rename: useMutation({
      mutationFn: (body: RenameReportBody) => api.renameReport(reportId, body),
      ...settle,
    }),
    preferences: useMutation({
      mutationFn: (body: ReportPreferencesBody) => api.setReportPreferences(reportId, body),
      ...settle,
    }),
    archive: useMutation({
      mutationFn: () => api.archiveReport(reportId),
      ...settle,
    }),
    cancel: useMutation({
      mutationFn: (runId: string) => api.cancelRun(runId),
      ...settle,
    }),
  };
}
